import { useState, useRef, useEffect } from 'react';
import type { ForecastOutput, ForecastEvent } from '../lib/forecast';
import { formatMoney, formatDate, addMonthsIso } from '../lib/format';
import type { Placement, Currency } from '../lib/types';

interface Props {
  forecast: ForecastOutput;
  currency: Currency;
  viewDate: string;
  today: string;
  onEventClick?: (placement: Placement) => void;
}

// SVG layout constants (px)
const HEIGHT = 420;
const PAD_LEFT = 60;
const PAD_RIGHT = 24;
const STARTER_LANE_Y = 28;
const FINISHER_LANE_Y = 56;
const LANE_LABEL_X = 4;
const CHART_TOP = 90;
const CHART_BOTTOM = 360;
const X_AXIS_LABEL_Y = 380;


function isoToMs(iso: string): number {
  return new Date(iso + 'T00:00:00Z').getTime();
}

/** First-of-month timestamps inside [startMs, endMs] for X-axis labels. */
function monthBoundaries(startMs: number, endMs: number): { t: number; label: string }[] {
  const start = new Date(startMs);
  const ticks: { t: number; label: string }[] = [];
  let cursor: Date;
  if (start.getUTCDate() === 1) {
    cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
  } else {
    cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1));
  }
  while (cursor.getTime() <= endMs) {
    ticks.push({
      t: cursor.getTime(),
      label: cursor.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }),
    });
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }
  return ticks;
}

/** "Nice" round Y-axis ticks across [0, maxValue]. */
function niceYTicks(maxValue: number): number[] {
  if (maxValue <= 0) return [0];
  const target = 4;
  const raw = maxValue / target;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  let step: number;
  if (norm < 1.5) step = mag;
  else if (norm < 3.5) step = 2 * mag;
  else if (norm < 7.5) step = 5 * mag;
  else step = 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v <= maxValue + 0.5 * step; v += step) ticks.push(v);
  return ticks;
}

/** Colour-blind-safe teal / vermillion (match the monitor's palette). */
const STARTER_COLOUR = 'var(--rag-green)';
const FINISHER_COLOUR = 'var(--rag-red)';

/** Equilateral-ish triangle centred on (cx, cy), pointing up or down. */
function triangle(cx: number, cy: number, r: number, dir: 'up' | 'down'): string {
  const h = r * 1.15;
  const w = r * 1.1;
  return dir === 'up'
    ? `M ${cx} ${cy - h} L ${cx + w} ${cy + h * 0.65} L ${cx - w} ${cy + h * 0.65} Z`
    : `M ${cx} ${cy + h} L ${cx + w} ${cy - h * 0.65} L ${cx - w} ${cy - h * 0.65} Z`;
}

/** Small inline legend glyph matching the chart markers. */
export function MarkerGlyph({ kind }: { kind: 'start' | 'finish' | 'extended' }) {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" style={{ flexShrink: 0 }}>
      {kind === 'start' ? (
        <path d={triangle(7, 7.5, 5, 'up')} style={{ fill: STARTER_COLOUR }} />
      ) : (
        <path
          d={triangle(7, 6.5, 5, 'down')}
          style={{ fill: FINISHER_COLOUR, stroke: FINISHER_COLOUR }}
          fillOpacity={0.18}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeDasharray={kind === 'extended' ? '2 1.6' : undefined}
        />
      )}
    </svg>
  );
}

function dotRadius(splitValue: number, maxSplit: number): number {
  if (maxSplit <= 0) return 5;
  const min = 4;
  const max = 9;
  return min + (Math.max(splitValue, 0) / maxSplit) * (max - min);
}

function formatTickValue(v: number): string {
  if (v >= 1000000) return `${(v / 1000000).toFixed(1)}m`;
  if (v >= 1000) return `${(v / 1000).toFixed(0)}k`;
  return String(Math.round(v));
}

interface LineHover {
  kind: 'line';
  x: number;
  y: number;
  date: string;
  book: number;
}

interface DotHover {
  kind: 'dot';
  x: number;
  y: number;
  event: ForecastEvent;
}

type Hover = LineHover | DotHover | null;

export function ForecastChart({ forecast, currency, viewDate, onEventClick }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<Hover>(null);

  // Track container width for responsive SVG
  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Window: always [viewDate, viewDate + 12 months]
  const viewStart = viewDate;
  const viewEnd = addMonthsIso(viewDate, 12);
  const viewStartMs = isoToMs(viewStart);
  const viewEndMs = isoToMs(viewEnd);

  const chartLeft = PAD_LEFT;
  const chartRight = Math.max(width - PAD_RIGHT, PAD_LEFT + 100);
  const chartW = chartRight - chartLeft;

  const xScale = (t: number): number =>
    chartLeft + ((t - viewStartMs) / (viewEndMs - viewStartMs)) * chartW;
  const inverseXScale = (px: number): number =>
    viewStartMs + ((px - chartLeft) / chartW) * (viewEndMs - viewStartMs);

  const timeline = forecast.timeline;
  const maxBook = Math.max(...timeline.map((p) => p.bookValue), 0);
  const yMax = maxBook > 0 ? maxBook * 1.1 : 1;
  const yTicks = niceYTicks(maxBook);

  const yScale = (book: number): number =>
    CHART_BOTTOM - (book / yMax) * (CHART_BOTTOM - CHART_TOP);

  // Build the step line path: stepAfter through every timeline anchor
  let linePath = '';
  if (timeline.length > 0) {
    const parts: string[] = [];
    for (let i = 0; i < timeline.length; i++) {
      const p = timeline[i];
      const x = xScale(isoToMs(p.date));
      const y = yScale(p.bookValue);
      if (i === 0) {
        parts.push(`M ${x} ${y}`);
      } else {
        const prev = timeline[i - 1];
        parts.push(`L ${x} ${yScale(prev.bookValue)}`);
        parts.push(`L ${x} ${y}`);
      }
    }
    linePath = parts.join(' ');
  }

  const months = monthBoundaries(viewStartMs, viewEndMs);
  const starters = forecast.events.filter((e) => e.kind === 'start');
  const finishers = forecast.events.filter((e) => e.kind === 'finish');
  const maxSplit = Math.max(...forecast.events.map((e) => e.splitValue), 1);

  // Mouse handler for the chart hit-area: snap to nearest Monday
  const onChartMouseMove = (e: React.MouseEvent<SVGRectElement>) => {
    const svg = e.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = e.clientX - rect.left;
    if (forecast.mondaySnapshots.length === 0) {
      setHover(null);
      return;
    }
    const cursorT = inverseXScale(px);
    let best = forecast.mondaySnapshots[0];
    let bestDiff = Math.abs(isoToMs(best.date) - cursorT);
    for (const m of forecast.mondaySnapshots) {
      const d = Math.abs(isoToMs(m.date) - cursorT);
      if (d < bestDiff) {
        best = m;
        bestDiff = d;
      }
    }
    setHover({
      kind: 'line',
      x: xScale(isoToMs(best.date)),
      y: yScale(best.bookValue),
      date: best.date,
      book: best.bookValue,
    });
  };

  const onChartMouseLeave = () => setHover(null);

  // Tooltip placement (clamped to container)
  const tooltipMaxWidth = 260;
  const tooltipLeft = hover
    ? Math.max(8, Math.min(hover.x + 12, width - tooltipMaxWidth - 8))
    : 0;
  const tooltipTop = hover
    ? hover.kind === 'line'
      ? Math.max(8, hover.y - 60)
      : hover.y + 14
    : 0;

  return (
    <section>
      <div className="ds-sec">
        <h2>
          Book value <em>over the next 12 months.</em>
        </h2>
        <span style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MarkerGlyph kind="start" /> Starter
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MarkerGlyph kind="finish" /> Finisher
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <MarkerGlyph kind="extended" /> Finishes after projected renewal
          </span>
          <span>bigger marker = bigger split · click one for the placement</span>
        </span>
      </div>

        <div className="ds-panel">
          <div ref={containerRef} className="relative w-full" style={{ height: HEIGHT }}>
            <svg width={width} height={HEIGHT} style={{ display: 'block' }}>
              {/* Lane labels */}
              <text
                x={LANE_LABEL_X}
                y={STARTER_LANE_Y + 3}
                fontSize={9}
                fontFamily="JetBrains Mono"
                style={{ letterSpacing: '0.1em', fill: 'var(--chart-label)' }}
              >
                START
              </text>
              <text
                x={LANE_LABEL_X}
                y={FINISHER_LANE_Y + 3}
                fontSize={9}
                fontFamily="JetBrains Mono"
                style={{ letterSpacing: '0.1em', fill: 'var(--chart-label)' }}
              >
                FINISH
              </text>

              {/* Subtle lane guide lines */}
              <line
                x1={chartLeft}
                x2={chartRight}
                y1={STARTER_LANE_Y}
                y2={STARTER_LANE_Y}
                style={{ stroke: 'var(--chart-grid)' }}
                strokeWidth={1}
              />
              <line
                x1={chartLeft}
                x2={chartRight}
                y1={FINISHER_LANE_Y}
                y2={FINISHER_LANE_Y}
                style={{ stroke: 'var(--chart-grid)' }}
                strokeWidth={1}
              />

              {/* Y-axis ticks (gridlines + labels) */}
              {yTicks.map((v) => (
                <g key={v}>
                  <line
                    x1={chartLeft}
                    x2={chartRight}
                    y1={yScale(v)}
                    y2={yScale(v)}
                    style={{ stroke: 'var(--chart-grid)' }}
                    strokeWidth={1}
                  />
                  <text
                    x={chartLeft - 8}
                    y={yScale(v) + 3}
                    style={{ fill: 'var(--chart-label)' }}
                    fontSize={10}
                    fontFamily="JetBrains Mono"
                    textAnchor="end"
                  >
                    {formatTickValue(v)}
                  </text>
                </g>
              ))}

              {/* X-axis labels */}
              {months.map((m) => (
                <text
                  key={m.t}
                  x={xScale(m.t)}
                  y={X_AXIS_LABEL_Y}
                  style={{ fill: 'var(--chart-label)' }}
                  fontSize={10}
                  fontFamily="JetBrains Mono"
                  textAnchor="middle"
                >
                  {m.label}
                </text>
              ))}

              {/* Step line for book value */}
              {linePath && (
                <path
                  d={linePath}
                  fill="none"
                  style={{ stroke: 'var(--chart-line)', opacity: 0.9 }}
                  strokeWidth={2}
                />
              )}

              {/* Hover indicator: vertical guide + active dot on line */}
              {hover?.kind === 'line' && (
                <>
                  <line
                    x1={hover.x}
                    x2={hover.x}
                    y1={CHART_TOP}
                    y2={CHART_BOTTOM}
                    style={{ stroke: 'var(--chart-label)' }}
                    strokeWidth={1}
                    strokeDasharray="3 3"
                  />
                  <circle
                    cx={hover.x}
                    cy={hover.y}
                    r={4}
                    style={{ fill: 'var(--chart-line)', stroke: 'var(--panel)' }}
                    strokeWidth={2}
                  />
                </>
              )}

              {/* Invisible hit area for line hover (Monday tooltip snapping) */}
              <rect
                x={chartLeft}
                y={CHART_TOP}
                width={chartW}
                height={CHART_BOTTOM - CHART_TOP}
                fill="transparent"
                onMouseMove={onChartMouseMove}
                onMouseLeave={onChartMouseLeave}
              />

              {/* Starters: solid triangles pointing up. Shape, not just colour,
                  tells them apart from finishers (colour-blind safe). */}
              {starters.map((e, idx) => {
                const t = isoToMs(e.date);
                if (t < viewStartMs || t > viewEndMs) return null;
                const cx = xScale(t);
                const r = dotRadius(e.splitValue, maxSplit) + 1;
                return (
                  <path
                    key={`s-${idx}`}
                    d={triangle(cx, STARTER_LANE_Y, r, 'up')}
                    fillOpacity={0.95}
                    style={{ fill: STARTER_COLOUR, cursor: onEventClick ? 'pointer' : 'default' }}
                    onMouseEnter={() => setHover({ kind: 'dot', x: cx, y: STARTER_LANE_Y, event: e })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => onEventClick?.(e.placement)}
                  />
                );
              })}

              {/* Finishers: outlined triangles pointing down; dashed when the
                  finish is a projected-renewal end rather than a real one. */}
              {finishers.map((e, idx) => {
                const t = isoToMs(e.date);
                if (t < viewStartMs || t > viewEndMs) return null;
                const cx = xScale(t);
                const r = dotRadius(e.splitValue, maxSplit) + 1;
                return (
                  <path
                    key={`f-${idx}`}
                    d={triangle(cx, FINISHER_LANE_Y, r, 'down')}
                    fillOpacity={0.18}
                    strokeWidth={1.8}
                    strokeLinejoin="round"
                    strokeDasharray={e.note === 'extended' ? '2.5 2' : undefined}
                    style={{ fill: FINISHER_COLOUR, stroke: FINISHER_COLOUR, cursor: onEventClick ? 'pointer' : 'default' }}
                    onMouseEnter={() => setHover({ kind: 'dot', x: cx, y: FINISHER_LANE_Y, event: e })}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => onEventClick?.(e.placement)}
                  />
                );
              })}
            </svg>

            {/* Floating tooltip overlay */}
            {hover && (
              <div
                style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  left: tooltipLeft,
                  top: tooltipTop,
                  maxWidth: tooltipMaxWidth,
                  background: 'var(--panel)',
                  border: '1px solid var(--border-strong)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  fontSize: 13,
                  boxShadow: 'var(--shadow)',
                }}
              >
                {hover.kind === 'line' && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4 }}>{formatDate(hover.date)}</div>
                    <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 16 }}>
                      {formatMoney(hover.book, currency)}
                      <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-2)' }}>/wk</span>
                    </div>
                  </>
                )}
                {hover.kind === 'dot' && (
                  <>
                    <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MarkerGlyph kind={hover.event.kind === 'start' ? 'start' : hover.event.note === 'extended' ? 'extended' : 'finish'} />
                      {hover.event.kind === 'start' ? 'Starter' : 'Finisher'} · {formatDate(hover.event.date)}
                    </div>
                    <div style={{ fontWeight: 500 }}>
                      {hover.event.placement.candidateName ?? hover.event.placement.jobTitle}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                      {hover.event.placement.jobTitle}
                      {hover.event.placement.clientName ? ` · ${hover.event.placement.clientName}` : ''}
                    </div>
                    <div style={{ fontFamily: 'var(--mono)', marginTop: 6 }}>
                      {formatMoney(hover.event.splitValue, currency, true)}
                      <span style={{ fontSize: 12, color: 'var(--text-2)' }}> WGP split</span>
                    </div>
                    {hover.event.note === 'extended' && (
                      <div style={{ fontSize: 12, color: 'var(--perm-ink)', marginTop: 4 }}>Extended +6 months</div>
                    )}
                    <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 6 }}>Click for details</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
    </section>
  );
}
